import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/store";
import { detectIncognitoMode, getAuthSession } from "@/utils/auth";
import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LoginForm } from "../../components/forms/LoginForm";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isIncognito, setIsIncognito] = useState(false);
  const [sessionError, setSessionError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const {
    isAuthenticated,
    initialized,
    initialize,
    user,
    setUser,
    setSession,
  } = useAuthStore();

  // Check if session expired message should be shown
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("reason") === "session_expired") {
      setSessionExpired(true);
      // Clean up the URL without reloading
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  useEffect(() => {
    // Check for loop detection parameter
    const urlParams = new URLSearchParams(window.location.search);
    const loopDetected = urlParams.get("loop_detected") === "true";

    if (loopDetected) {
      console.warn(
        "Auth loop detected and broken. Starting fresh login process."
      );
      setIsLoading(false);
      // Force reset the auth state to prevent further loops
      localStorage.removeItem("auth-storage");
      setUser(null);
      setSession(null);
      return;
    }

    // Simple debounce/anti-loop mechanism
    let isMounted = true;
    let sessionCheckTimeout = null;

    const checkSession = async () => {
      try {
        // Use the centralized auth session check
        const { session, error: sessionError } = await getAuthSession(supabase);

        // Don't proceed if component has unmounted
        if (!isMounted) return;

        if (sessionError) {
          console.error("Error getting session:", sessionError);
          setSessionError(sessionError.message);
          setIsLoading(false);
          return;
        }

        if (session) {
          // Session exists - verify it's valid by attempting to get user
          const { data: userData, error: userError } =
            await supabase.auth.getUser();

          if (!isMounted) return;

          if (userError) {
            console.error("Session appears invalid:", userError);
            // Session exists but is invalid, clear it
            await supabase.auth.signOut();
            setIsLoading(false);
            return;
          }

          // We have a valid session and user - ensure store is updated
          if (!initialized || !user) {
            setUser(userData.user);
            setSession(session);

            // Then initialize the rest
            await initialize();
          }

          // Safely redirect with loop prevention
          const loopCount = parseInt(location.state?.loop_count || "0");
          if (loopCount > 2) {
            console.error(
              "Detected potential login loop, staying on login page"
            );
            setIsLoading(false);
            return;
          }

          // Fetch the user's profile to check onboarding status
          const { data: profile, error: profileError } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", userData.user.id)
            .single();

          if (profileError) {
            // If we can't fetch profile, default to chat and let ProtectedRoute handle it
            const redirectTo = location.state?.from?.pathname || "/chat/new";
            const safePath = redirectTo.startsWith("/auth")
              ? "/chat/new"
              : redirectTo;

            sessionCheckTimeout = setTimeout(() => {
              if (isMounted) {
                navigate(safePath, {
                  replace: true,
                  state: { sessionConfirmed: true },
                });
              }
            }, 50);
            return;
          }

          // CRITICAL: Initialize the app immediately after we have the profile
          await initialize();

          // Determine redirect based on onboarding status
          let targetPath;
          if (!profile.onboarding_completed) {
            targetPath = "/auth/onboarding";
          } else {
            const redirectTo = location.state?.from?.pathname || "/chat/new";
            targetPath = redirectTo.startsWith("/auth")
              ? "/chat/new"
              : redirectTo;
          }

          sessionCheckTimeout = setTimeout(() => {
            if (isMounted) {
              navigate(targetPath, {
                replace: true,
                state: { sessionConfirmed: true },
              });
            }
          }, 50);
        } else {
          // No valid session
          setIsLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          setSessionError(error.message);
          setIsLoading(false);
        }
      }
    };

    const checkIncognitoMode = async () => {
      try {
        const incognitoStatus = await detectIncognitoMode();
        if (isMounted) {
          setIsIncognito(incognitoStatus);
        }
      } catch (error) {
        console.error("Error detecting incognito mode:", error);
      }
    };

    // Run session check after a short delay to avoid race conditions
    const initTimeout = setTimeout(() => {
      checkSession();
      checkIncognitoMode();
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
      if (sessionCheckTimeout) clearTimeout(sessionCheckTimeout);
    };
  }, [navigate, location, initialized, initialize, user, setUser, setSession]);

  // if (isLoading) {
  //   return (
  //     <div className="h-screen flex flex-col items-center justify-center">
  //       {isLoading && (
  //         <div className="fixed inset-0 z-40 flex items-center justify-center bg-background/20 backdrop-blur-[2px]">
  //           <div className="w-8 h-8 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
  //         </div>
  //       )}
  //     </div>
  //   );
  // }

  return (
    <>
      {/* {sessionError && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error checking session: {sessionError}. You may need to clear your
            browser cookies.
          </AlertDescription>
        </Alert>
      )} */}

      {sessionExpired && (
        <Alert variant="warning" className="mb-4 mx-4 max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Your session has expired. Please log in again to continue.
          </AlertDescription>
        </Alert>
      )}

      {isIncognito && (
        <Alert variant="warning" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You&apos;re using incognito/private browsing mode. If you experience
            any issues with login or signup, try using regular browsing mode or
            a different browser.
          </AlertDescription>
        </Alert>
      )}

      <LoginForm />
    </>
  );
}

export default LoginPage;
